import * as XLSX from 'xlsx';

/** Row shape returned by GET /bank-statements/export. */
export interface BankExportRow {
  transaction_date: string;
  description: string | null;
  voucher_number: string | null;
  deposit_amount: number | null;
  withdrawal_amount: number | null;
  balance: number | null;
}

const HEADER = ['Date', 'Description', 'Voucher', 'Deposit', 'Withdrawal', 'Balance'];

/** Build the single-sheet workbook data: header + one row per transaction.
 *  Amounts stay numbers so Excel can sum them; nulls become blank cells. */
export function buildBankExportSheet(rows: BankExportRow[]): XLSX.WorkSheet {
  const aoa: (string | number)[][] = [
    HEADER,
    ...rows.map(r => [
      r.transaction_date,
      r.description ?? '',
      r.voucher_number ?? '',
      r.deposit_amount ?? '',
      r.withdrawal_amount ?? '',
      r.balance ?? '',
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 12 }, { wch: 50 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
  return ws;
}

/** Generate and download the .xlsx for a date range. */
export function exportBankStatementsXlsx(rows: BankExportRow[], startDate: string, endDate: string): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildBankExportSheet(rows), 'Bank Statements');
  XLSX.writeFile(wb, `bank-statements_${startDate}_to_${endDate}.xlsx`);
}

/** Row shape carried by the statement detail endpoint. */
export interface StatementTxExportInput {
  transaction_date: string;
  description?: string | null;
  deposit_amount?: number | null;
  withdrawal_amount?: number | null;
  balance?: number | null;
  voucher_number?: string | null;
  voucher?: string | null;
  posting?: { entry_number?: string | null } | null;
}

/** Map loaded statement transactions onto the shared bank export columns. */
export function buildStatementExportRows(txs: StatementTxExportInput[]): BankExportRow[] {
  return txs.map(tx => ({
    transaction_date: tx.transaction_date,
    description: tx.description ?? '',
    voucher_number: tx.voucher_number ?? tx.voucher ?? tx.posting?.entry_number ?? null,
    deposit_amount: tx.deposit_amount ?? null,
    withdrawal_amount: tx.withdrawal_amount ?? null,
    balance: tx.balance ?? null,
  }));
}

/** Download one statement's transactions as a real .xlsx workbook. */
export function exportStatementTransactionsXlsx(txs: StatementTxExportInput[], label: string): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildBankExportSheet(buildStatementExportRows(txs)), 'Statement');
  XLSX.writeFile(wb, `bank-statement_${label}.xlsx`);
}
